import { PayloadAction } from "@reduxjs/toolkit";
import { Insight } from "@stratify/shared";
import { call, put, takeLatest, takeLeading } from "redux-saga/effects";
import { insightsService } from "../insightsService";
import {
  createInsightFailure,
  createInsightStart,
  createInsightSuccess,
  deleteInsightFailure,
  deleteInsightStart,
  deleteInsightSuccess,
  fetchInsightsFailure,
  fetchInsightsStart,
  fetchInsightsSuccess,
  fetchSingleInsightFailure,
  fetchSingleInsightStart,
  fetchSingleInsightSuccess,
  updateInsightFailure,
  updateInsightStart,
  updateInsightSuccess,
} from "./insightsSlice";

function* fetchInsightsSaga(action: PayloadAction<string>) {
  try {
    const insights: Insight[] = yield call(
      insightsService.getInsightsByDocument,
      action.payload
    );
    yield put(fetchInsightsSuccess({ documentId: action.payload, insights }));
  } catch (error: any) {
    yield put(
      fetchInsightsFailure(error.message || "Failed to fetch insights")
    );
  }
}

function* createInsightSaga(
  action: PayloadAction<Omit<Insight, "id" | "createdAt" | "updatedAt">>
) {
  try {
    const { documentId, ...insightData } = action.payload;
    const insight: Insight = yield call(
      insightsService.createInsight,
      documentId,
      insightData
    );
    yield put(createInsightSuccess(insight));
  } catch (error: any) {
    yield put(
      createInsightFailure(error.message || "Failed to create insight")
    );
  }
}

function* updateInsightSaga(
  action: PayloadAction<{
    documentId: string;
    insightId: string;
    updates: Partial<Insight>;
  }>
) {
  try {
    const { documentId, insightId, updates } = action.payload;
    yield call(insightsService.updateInsight, documentId, insightId, updates);
    yield put(updateInsightSuccess({ documentId, insightId, updates }));
  } catch (error: any) {
    yield put(
      updateInsightFailure(error.message || "Failed to update insight")
    );
  }
}

function* deleteInsightSaga(
  action: PayloadAction<{ documentId: string; insightId: string }>
) {
  try {
    const { documentId, insightId } = action.payload;
    yield call(insightsService.deleteInsight, documentId, insightId);
    yield put(deleteInsightSuccess({ documentId, insightId }));
  } catch (error: any) {
    yield put(
      deleteInsightFailure(error.message || "Failed to delete insight")
    );
  }
}

function* fetchSingleInsightSaga(action: PayloadAction<string>) {
  try {
    const insightId = action.payload;
    const insight: Insight = yield call(
      insightsService.getInsightById,
      insightId
    );
    yield put(fetchSingleInsightSuccess(insight));
  } catch (error: any) {
    yield put(
      fetchSingleInsightFailure(error.message || "Failed to fetch insight")
    );
  }
}

export function* insightsSaga() {
  yield takeLatest(fetchInsightsStart.type, fetchInsightsSaga);
  yield takeLatest(createInsightStart.type, createInsightSaga);
  yield takeLatest(updateInsightStart.type, updateInsightSaga);
  yield takeLatest(deleteInsightStart.type, deleteInsightSaga);
  yield takeLeading(fetchSingleInsightStart.type, fetchSingleInsightSaga);
}
